import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Skeleton } from '@mui/material';
import { Header } from '@/components/layout';
import { useAnalyses } from '@/api/domains/analyses';
import { AnalysisStatus } from '@/api/domains/analyses/types';
import { useFilteredAnalyses } from './hooks/useFilteredAnalyses';
import {
  StatusFilter,
  SearchBar,
  AnalysisCard,
  EmptyState
} from './components';
import styles from './AnalysesList.module.css';
import { BackButton } from '@/components/ui';

const AnalysesList = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<AnalysisStatus | 'all'>(
    'all'
  );
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading, error } = useAnalyses({
    skip: 0,
    limit: 100
  });

  const filteredAnalyses = useFilteredAnalyses({
    analyses: data?.analyses,
    statusFilter,
    searchQuery
  });

  return (
    <div className={styles.root}>
      <Header />
      <main className={styles.main}>
        <div className={styles.backButtonContainer}>
          <BackButton to="/" />
        </div>
        <section className={styles.pageHeader}>
          <div>
            <h1>Analyses</h1>
            <p className={styles.subtitle}>
              Document comparison and gap analysis results
            </p>
            {!isLoading && data && (
              <div className={styles.stats}>
                <span className={styles.totalCount}>
                  <i className="pi pi-chart-line" />
                  {filteredAnalyses.length}{' '}
                  {filteredAnalyses.length === 1 ? 'Analysis' : 'Analyses'}
                  {statusFilter !== 'all' && (
                    <span style={{ fontWeight: 400, opacity: 0.7 }}>
                      {' '}
                      ({data.analyses.length} total)
                    </span>
                  )}
                </span>
              </div>
            )}
          </div>
          <div className={styles.headerActions}>
            <StatusFilter
              statusFilter={statusFilter}
              onChange={setStatusFilter}
            />
            <Button
              variant="contained"
              onClick={() => navigate('/analyses/create')}
              className={styles.newButton}
            >
              New Analysis
            </Button>
          </div>
        </section>

        <section className={styles.toolbarRow}>
          <SearchBar searchQuery={searchQuery} onChange={setSearchQuery} />
        </section>

        {isLoading && (
          <div className={styles.analysesGrid}>
            {[...Array(6)].map((_, i) => (
              <Card key={i} className={styles.analysisCard}>
                <div className={styles.loadingSkeleton}>
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height="100%"
                    animation="wave"
                    sx={{
                      bgcolor: 'var(--color-surface-card)',
                      borderRadius: 1
                    }}
                  />
                </div>
              </Card>
            ))}
          </div>
        )}

        {error && (
          <div className={styles.errorBox}>
            <i className="pi pi-exclamation-triangle" />
            <div>
              <h3>Failed to load analyses</h3>
              <p>{error.message}</p>
            </div>
          </div>
        )}

        {!isLoading && data && filteredAnalyses.length > 0 && (
          <div className={styles.analysesGrid}>
            {filteredAnalyses.map((analysis) => (
              <AnalysisCard key={analysis.analysis_id} analysis={analysis} />
            ))}
          </div>
        )}

        {!isLoading && data && filteredAnalyses.length === 0 && (
          <EmptyState
            statusFilter={statusFilter}
            onClearFilter={() => setStatusFilter('all')}
          />
        )}
      </main>
    </div>
  );
};

export default AnalysesList;
